import React, { useState, useEffect } from 'react';
import { Heart, TrendingUp, Users, Star, Search, Share2, Phone, Award, Shield, Calendar, Clock, Activity, Lock, X, Droplet, MapPin, Plus, Bell, ArrowRight, CheckCircle, Hospital, MoreVertical } from 'react-feather';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import api from '../../../config/api';

const Overview = () => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    donor: {
      name: '',
      bloodType: '',
      lastDonation: null,
      totalDonations: 0,
      donationStreak: 0,
      nextEligibleDate: new Date().toISOString()
    },
    donationHistory: [],
    acceptedRequests: [],
    nearbyRequests: [],
    upcomingAppointments: [],
    upcomingEvents: [],
    impactMetrics: {
      totalDonations: 0,
      livesSaved: 0,
      hospitalsServed: 0,
      successRate: 0
    },
    monthlyDonations: Array(12).fill(0),
    achievements: [],
    yoyGrowth: 0
  });

  useEffect(() => {
    fetchDonorDashboard();
  }, []);

  const fetchDonorDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/donor');
      setDashboardData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching donor dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        toast.error('All password fields are required');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }

      // Here you would typically make an API call to update the password
      // Add your API call here

      toast.success('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordModal(false);
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <X className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Add null checks and default values for all dashboard data
  const {
    donor = {
      name: '',
      bloodType: '',
      lastDonation: null,
      totalDonations: 0,
      donationStreak: 0,
      nextEligibleDate: new Date().toISOString()
    },
    impactMetrics = {
      totalDonations: 0,
      livesSaved: 0,
      hospitalsServed: 0,
      successRate: 0
    },
    monthlyDonations = Array(12).fill(0),
    achievements = [],
    upcomingAppointments = [],
    yoyGrowth = 0
  } = dashboardData || {};

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Enhanced Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Donations Card */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Donations</p>
              <h3 className="text-xl font-bold text-gray-900">{donor.totalDonations}</h3>
              <p className="text-[10px] text-gray-600 mt-0.5">Lifetime contributions</p>
            </div>
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center text-xs text-green-600">
              <TrendingUp className="w-3.5 h-3.5 mr-1" />
              <span>{yoyGrowth}% {yoyGrowth >= 0 ? 'more' : 'less'} than last year</span>
            </div>
          </div>
        </motion.div>

        {/* Lives Impacted Card */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Lives Impacted</p>
              <h3 className="text-xl font-bold text-gray-900">{impactMetrics.livesSaved}</h3>
              <p className="text-[10px] text-gray-600 mt-0.5">Across {impactMetrics.hospitalsServed} hospitals</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center text-xs text-blue-600">
              <MapPin className="w-3.5 h-3.5 mr-1" />
              <span>{impactMetrics.hospitalsServed} hospitals served</span>
            </div>
          </div>
        </motion.div>

        {/* Next Eligible Date Card */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Next Eligible Date</p>
              <h3 className="text-lg font-bold text-gray-900">
                {formatDate(donor.nextEligibleDate)}
              </h3>
              <p className="text-[10px] text-gray-600 mt-0.5">
                {upcomingAppointments.length > 0 ? 'Appointment scheduled' : 'No appointment yet'}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center text-xs text-gray-600">
              <Clock className="w-3.5 h-3.5 mr-1" />
              <span>Based on last donation</span>
            </div>
          </div>
        </motion.div>

        {/* Success Rate Card */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Success Rate</p>
              <h3 className="text-xl font-bold text-gray-900">{impactMetrics.successRate}%</h3>
              <p className="text-[10px] text-gray-600 mt-0.5">Successful donations</p>
            </div>
            <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center text-xs text-purple-600">
              <Award className="w-3.5 h-3.5 mr-1" />
              <span>Donation streak: {donor.donationStreak}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Donation History Chart */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-base font-bold text-gray-900 mb-3">Donation History</h3>
        <div className="h-48">
          <div className="grid grid-cols-12 gap-2 h-32">
            {monthlyDonations.map((count, index) => (
              <div key={index} className="flex items-end h-full">
                <div
                  className="w-full bg-red-200 rounded-t"
                  style={{
                    height: `${count > 0 ? (count / Math.max(...monthlyDonations, 1)) * 100 : 0}%`,
                    minHeight: count > 0 ? '4px' : '0'
                  }}
                ></div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-gray-500">
            <span>Jan</span>
            <span>Feb</span>
            <span>Mar</span>
            <span>Apr</span>
            <span>May</span>
            <span>Jun</span>
            <span>Jul</span>
            <span>Aug</span>
            <span>Sep</span>
            <span>Oct</span>
            <span>Nov</span>
            <span>Dec</span>
          </div>
        </div>
      </motion.div>

      {/* Upcoming Appointments & Achievements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Upcoming Appointments */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-base font-bold text-gray-900 mb-3">Upcoming Appointments</h3>
          {upcomingAppointments.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Calendar className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No upcoming appointments</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((appointment, index) => (
                <div key={index} className="flex items-start p-2.5 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 bg-blue-100 p-1.5 rounded-lg mr-2.5">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">{appointment.hospital}</p>
                    <p className="text-xs text-gray-600">{formatDate(appointment.date)} at {appointment.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Achievements */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-base font-bold text-gray-900 mb-3">Recent Achievements</h3>
          {achievements.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Award className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No achievements yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {achievements.map((achievement, index) => (
                <div key={index} className="flex items-start p-2.5 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 bg-yellow-100 p-1.5 rounded-lg mr-2.5">
                    <Award className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">{achievement.title}</p>
                    <p className="text-xs text-gray-600">{achievement.description}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(achievement.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Overview;